import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X, RotateCcw, Minus, Plus,
  Type, Link as LinkIcon, MousePointer2, Pause,
} from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';

/* Custom lightweight SVG icons for glyph-based labels (T, A, B, |A|, Ā, etc.).
   Only accept safe SVG-only props (className, aria-hidden) to avoid passing
   invalid DOM attributes into <svg>. */
const svgProps = ({ className, 'aria-hidden': ariaHidden }) => ({ className, 'aria-hidden': ariaHidden });

/* Universal accessibility symbol — solid stick figure with outstretched arms
   and V-shaped legs, drawn as SVG so it inherits currentColor (matches the
   floating button's violet theme without a coloured background disc). */
const AccessibilityFigure = (p) => (
  <svg viewBox="0 0 32 32" fill="currentColor" {...svgProps(p)}>
    <circle cx="16" cy="6" r="2.6" />
    <path d="M6.5 10.2h19a1.6 1.6 0 0 1 0 3.2H19.6v4.4l3.5 8.4a1.5 1.5 0 0 1-2.77 1.16L16.7 19.6h-1.4l-3.63 7.76a1.5 1.5 0 0 1-2.77-1.16l3.5-8.4v-4.4H6.5a1.6 1.6 0 0 1 0-3.2z" />
  </svg>
);

const GlyphT = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)}><path d="M5 5h14v3.5h-4.5V21h-5V8.5H5V5z" fill="currentColor" /></svg>
);
const GlyphDyslexia = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)}>
    <path d="M8.5 5l-4 12h1.9l1.05-3.2h4.1L12.6 17h1.9l-4-12H8.5zm-.5 7.1L9.5 7.6l1.5 4.5H8z" fill="currentColor" />
    <path d="M15 13.5l1.5 1.5L20 11.5l-1-1-2.5 2.5-.5-.5-1 1z" fill="currentColor" />
  </svg>
);
const GlyphLetterSpacing = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <rect x="3" y="4" width="1.5" height="16" />
    <rect x="19.5" y="4" width="1.5" height="16" />
    <path d="M10 7l-3 10h1.6l.7-2.2h3.4l.7 2.2H15l-3-10h-2zm.3 6.3l1-3.5 1 3.5h-2z" />
  </svg>
);
const GlyphLineHeight = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <rect x="4" y="3" width="16" height="1.5" />
    <rect x="4" y="19.5" width="16" height="1.5" />
    <path d="M10 7l-3 10h1.6l.7-2.2h3.4l.7 2.2H15l-3-10h-2zm.3 6.3l1-3.5 1 3.5h-2z" />
  </svg>
);
const GlyphBold = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <path d="M7 5h6.5c2.5 0 4 1.4 4 3.4 0 1.4-.7 2.4-1.9 2.9 1.5.5 2.4 1.7 2.4 3.2 0 2.3-1.7 3.5-4.5 3.5H7V5zm3 5.4h3.2c1.1 0 1.8-.6 1.8-1.5s-.7-1.5-1.8-1.5H10v3zm0 5.6h3.5c1.3 0 2-.6 2-1.7s-.7-1.7-2-1.7H10V16z" />
  </svg>
);
const GlyphDarkContrast = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <ellipse cx="12" cy="12" rx="7" ry="8" />
  </svg>
);
const GlyphLightContrast = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <path d="M12 6c.6 0 1 .4 1 1v1c0 .6-.4 1-1 1s-1-.4-1-1V7c0-.6.4-1 1-1zm5 5c0-.6.4-1 1-1h1c.6 0 1 .4 1 1s-.4 1-1 1h-1c-.6 0-1-.4-1-1zM6 12c0-.6-.4-1-1-1H4c-.6 0-1 .4-1 1s.4 1 1 1h1c.6 0 1-.4 1-1zm10.5-5.5c.4-.4 1-.4 1.4 0s.4 1 0 1.4l-.7.7c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l.7-.7zm-9 9c.4-.4 1-.4 1.4 0s.4 1 0 1.4l-.7.7c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l.7-.7zm10-.7c.4-.4 1-.4 1.4 0l.7.7c.4.4.4 1 0 1.4s-1 .4-1.4 0l-.7-.7c-.4-.4-.4-1 0-1.4zm-10.4-10.4c.4-.4 1-.4 1.4 0l.7.7c.4.4.4 1 0 1.4s-1 .4-1.4 0l-.7-.7c-.4-.4-.4-1 0-1.4zM12 15c-.6 0-1 .4-1 1v1c0 .6.4 1 1 1s1-.4 1-1v-1c0-.6-.4-1-1-1zm-4-3c0 2.2 1.8 4 4 4s4-1.8 4-4-1.8-4-4-4-4 1.8-4 4z" />
  </svg>
);
const GlyphHighContrast = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <path d="M12 4a8 8 0 100 16 8 8 0 000-16zm0 14V6a6 6 0 010 12z" />
  </svg>
);
const GlyphHighSaturation = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <path d="M12 3l2 3.5 4-.6-2 3.5 2 3.5-4-.6L12 15l-2-2.7-4 .6 2-3.5-2-3.5 4 .6L12 3z" />
  </svg>
);
const GlyphLowSaturation = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <rect x="3" y="3" width="4" height="4" /><rect x="11" y="3" width="4" height="4" opacity=".4" />
    <rect x="7" y="7" width="4" height="4" opacity=".4" /><rect x="15" y="7" width="4" height="4" />
    <rect x="3" y="11" width="4" height="4" /><rect x="11" y="11" width="4" height="4" opacity=".4" />
    <rect x="7" y="15" width="4" height="4" opacity=".4" /><rect x="15" y="15" width="4" height="4" />
  </svg>
);
const GlyphMonochrome = (p) => (
  <svg viewBox="0 0 24 24" {...svgProps(p)} fill="currentColor">
    <path d="M14 4l7 16H3l7-16 2 4 2-4z" />
  </svg>
);

/**
 * Floating accessibility widget — İDK BOKT (WCAG 2.1 AA).
 * Card-grid layout inspired by CERT.GOV.AZ; İDK brand blue.
 */
export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const { prefs, update, adjustFont, reset, isDefault } = useAccessibility();
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return createPortal(
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Əlçatanlıq alət panelini aç"
        aria-expanded={open}
        aria-controls="a11y-panel"
        data-testid="a11y-trigger-btn"
        className="fixed left-5 bottom-5 z-[60] flex items-center justify-center h-16 w-16 rounded-full bg-violet-600 text-white shadow-xl hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300 transition-colors"
      >
        <AccessibilityFigure className="h-9 w-9" aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id="a11y-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="a11y-panel-title"
          data-testid="a11y-panel"
          className="fixed left-5 bottom-24 z-[60] w-[400px] max-w-[94vw] max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-200 flex flex-col"
        >
          {/* Header — matches the trigger button (İDK brand blue) */}
          <div className="flex items-center justify-between px-5 py-4 bg-violet-600 text-white">
            <h2 id="a11y-panel-title" className="text-base font-semibold">
              Müyəssərlik Menyusu
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={reset}
                disabled={isDefault}
                aria-label="Bütün parametrləri sıfırla"
                data-testid="a11y-reset-btn"
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Paneli bağla"
                data-testid="a11y-close-btn"
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-5 overflow-y-auto bg-white">
            <div>
              <h3 className="text-xs text-gray-500 mb-2 pl-1">Məzmunun Tənzimlənməsi</h3>
              <FontSizeCard
                percent={prefs.fontPercent}
                onDecrease={() => adjustFont(-10)}
                onIncrease={() => adjustFont(10)}
              />
              <div className="grid grid-cols-3 gap-2 mt-2">
                <ToggleCard icon={GlyphT} label="Başlıqları Seçilən Et"
                  active={prefs.highlightTitles} onClick={() => update({ highlightTitles: !prefs.highlightTitles })}
                  testId="a11y-titles-toggle" />
                <ToggleCard icon={LinkIcon} label="Hiperlinkləri Seçilən Et"
                  active={prefs.highlightLinks} onClick={() => update({ highlightLinks: !prefs.highlightLinks })}
                  testId="a11y-links-toggle" />
                <ToggleCard icon={GlyphDyslexia} label="Disleksiya Şrifti"
                  active={prefs.dyslexiaFont} onClick={() => update({ dyslexiaFont: !prefs.dyslexiaFont })}
                  testId="a11y-dyslexia-toggle" />
                <ToggleCard icon={GlyphLetterSpacing} label="Hərf Aralığı"
                  active={prefs.letterSpacing} onClick={() => update({ letterSpacing: !prefs.letterSpacing })}
                  testId="a11y-letter-toggle" />
                <ToggleCard icon={GlyphLineHeight} label="Sətir Hündürlüyü"
                  active={prefs.lineHeight} onClick={() => update({ lineHeight: !prefs.lineHeight })}
                  testId="a11y-line-toggle" />
                <ToggleCard icon={GlyphBold} label="Şrift qalınlığı"
                  active={prefs.boldFont} onClick={() => update({ boldFont: !prefs.boldFont })}
                  testId="a11y-bold-toggle" />
              </div>
            </div>

            <div>
              <h3 className="text-xs text-gray-500 mb-2 pl-1">Rəng Tənzimlənməsi</h3>
              <div className="grid grid-cols-3 gap-2">
                <ExclusiveCard icon={GlyphDarkContrast} label="Tünd Kontrast"
                  active={prefs.colorMode === 'dark'} onClick={() => update({ colorMode: prefs.colorMode === 'dark' ? 'default' : 'dark' })}
                  testId="a11y-color-dark" />
                <ExclusiveCard icon={GlyphLightContrast} label="Açıq Kontrast"
                  active={prefs.colorMode === 'light'} onClick={() => update({ colorMode: prefs.colorMode === 'light' ? 'default' : 'light' })}
                  testId="a11y-color-light" />
                <ExclusiveCard icon={GlyphHighContrast} label="Yüksək Kontrast"
                  active={prefs.colorMode === 'high'} onClick={() => update({ colorMode: prefs.colorMode === 'high' ? 'default' : 'high' })}
                  testId="a11y-color-high" />
                <ExclusiveCard icon={GlyphHighSaturation} label="Yüksək Doyma"
                  active={prefs.saturation === 'high'} onClick={() => update({ saturation: prefs.saturation === 'high' ? 'default' : 'high' })}
                  testId="a11y-saturation-high" />
                <ExclusiveCard icon={GlyphLowSaturation} label="Aşağı Doyma"
                  active={prefs.saturation === 'low'} onClick={() => update({ saturation: prefs.saturation === 'low' ? 'default' : 'low' })}
                  testId="a11y-saturation-low" />
                <ExclusiveCard icon={GlyphMonochrome} label="Monoxrom"
                  active={prefs.saturation === 'mono'} onClick={() => update({ saturation: prefs.saturation === 'mono' ? 'default' : 'mono' })}
                  testId="a11y-saturation-mono" />
              </div>
            </div>

            <div>
              <h3 className="text-xs text-gray-500 mb-2 pl-1">Alətlər</h3>
              <div className="grid grid-cols-3 gap-2">
                <ToggleCard icon={MousePointer2} label="Böyük Kursor"
                  active={prefs.bigCursor} onClick={() => update({ bigCursor: !prefs.bigCursor })}
                  testId="a11y-cursor-toggle" />
                <ToggleCard icon={Pause} label="Animasiyaları Dayandır"
                  active={prefs.stopAnimations} onClick={() => update({ stopAnimations: !prefs.stopAnimations })}
                  testId="a11y-motion-toggle" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

const FontSizeCard = ({ percent, onDecrease, onIncrease }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <div className="flex items-center gap-2 mb-2 text-sm text-gray-700">
      <Type className="h-4 w-4 text-gray-500" aria-hidden="true" />
      <span>Şrift Ölçüsünü Tənzimlə</span>
    </div>
    <div className="grid grid-cols-3 items-center gap-2">
      <button
        type="button"
        onClick={onDecrease}
        disabled={percent <= 90}
        aria-label="Şrifti kiçilt"
        data-testid="a11y-font-decrease"
        className="h-10 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 transition-colors"
      >
        <Minus className="h-4 w-4 text-gray-700" aria-hidden="true" />
      </button>
      <div className="text-center text-base font-semibold text-gray-900" data-testid="a11y-font-percent">
        {percent}%
      </div>
      <button
        type="button"
        onClick={onIncrease}
        disabled={percent >= 150}
        aria-label="Şrifti böyüt"
        data-testid="a11y-font-increase"
        className="h-10 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 transition-colors"
      >
        <Plus className="h-4 w-4 text-gray-700" aria-hidden="true" />
      </button>
    </div>
  </div>
);

const CardBase = ({ active, onClick, testId, ariaChecked, role, children }) => (
  <button
    type="button"
    role={role}
    aria-checked={ariaChecked}
    onClick={onClick}
    data-testid={testId}
    className={`flex flex-col items-center justify-center gap-2 h-[104px] rounded-xl border p-2 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
      active
        ? 'border-violet-600 bg-violet-50 text-violet-700 shadow-sm'
        : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 shadow-sm'
    }`}
  >
    {children}
  </button>
);

const ToggleCard = ({ icon: Icon, label, active, onClick, testId }) => (
  <CardBase active={active} onClick={onClick} testId={testId} ariaChecked={active} role="switch">
    <Icon className={`h-8 w-8 ${active ? 'text-violet-600' : 'text-gray-700'}`} aria-hidden="true" />
    <span className="text-[11px] font-medium leading-tight">{label}</span>
  </CardBase>
);

const ExclusiveCard = ({ icon: Icon, label, active, onClick, testId }) => (
  <CardBase active={active} onClick={onClick} testId={testId} ariaChecked={active} role="switch">
    <Icon className={`h-8 w-8 ${active ? 'text-violet-600' : 'text-gray-700'}`} aria-hidden="true" />
    <span className="text-[11px] font-medium leading-tight">{label}</span>
  </CardBase>
);
